import { useAppState } from '../../../state';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';
import { confirmConsentForDataProcessing } from '../../../utils/requests';
import { appServer } from '../../../utils/api';
import { ConsentForDataProcessingConfirmed, UserRole } from '../../../types';
import { useSnackbar } from '@upheal/design-system/vendor/notistack';
import { isWhiteLabelApp } from '@upheal/design-system/theme/legacy/is-whitelabel';
import { captureException } from '../../../utils/sentry';
import { useFeatureFlagEnabled } from 'posthog-js/react';

type DialogControlOptions = {
    shouldBlockClosingDialog: boolean;
};

export type ConsentConfirmation = {
    isConsentConfirmationOpened: boolean;
    setIsConsentConfirmationOpened: (
        open: boolean,
        options?: DialogControlOptions,
    ) => void;
    confirmConsent: () => Promise<void>;
    isNewUser: boolean;
    shouldDisplayConsentConfirmation: boolean;
    shouldKeepOpen: boolean;
    shouldInterruptActionAndOpenConsentConfirmation: () => Promise<unknown>;
    isProcessingConfirmation: boolean;
};

export const ConsentConfirmationContext = createContext<
    ConsentConfirmation | undefined
>(undefined);

export const ConsentConfirmationProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { user, updateUser } = useAppState();
    const [open, setOpen] = useState(false);
    const [shouldKeepOpen, setShouldKeepOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [isProcessingConfirmation, setIsProcessingConfirmation] =
        useState(false);
    const interruptionPromise = useRef<
        ((value: boolean | PromiseLike<boolean>) => void) | null
    >(null);

    const isClientConsentDisabled = useFeatureFlagEnabled(
        'disable-feature-client-consent',
    );

    const userConsentForDataProcessingConfirmed =
        user?.consentForDataProcessingConfirmed;

    const isNewUnconfirmedUser =
        userConsentForDataProcessingConfirmed === 'Pending';
    const isOldUnconfirmedUser =
        userConsentForDataProcessingConfirmed === 'PendingExisting';
    const shouldDisplayConsentConfirmation =
        !isClientConsentDisabled &&
        !isWhiteLabelApp &&
        (user?.role === UserRole.OrgAdmin ||
            user?.role === UserRole.HealthcareProvider) &&
        (isNewUnconfirmedUser || isOldUnconfirmedUser);

    const confirmConsent = useCallback(async () => {
        try {
            setIsProcessingConfirmation(true);
            const response = await appServer.request<{
                status: ConsentForDataProcessingConfirmed;
            }>(confirmConsentForDataProcessing());

            updateUser((draft) => {
                if (draft) {
                    draft.consentForDataProcessingConfirmed =
                        response.data.status;
                }
            });

            if (interruptionPromise.current) {
                interruptionPromise.current(false);
                interruptionPromise.current = null;
            }
        } catch (error) {
            captureException('Failed to confirm consents', {
                error,
            });
            enqueueSnackbar('Failed to confirm consents. Please try again.', {
                variant: 'error',
            });

            if (interruptionPromise.current) {
                interruptionPromise.current(true);
                interruptionPromise.current = null;
            }

            throw error;
        } finally {
            setShouldKeepOpen(false);
            setOpen(false);
            setIsProcessingConfirmation(false);
        }
    }, [enqueueSnackbar, updateUser]);

    /**
     * Will immediately display the consent confirmation dialog if needed
     *
     * @return true if action at the call site should be interrupted in favor of consent confirmation
     */
    const shouldInterruptActionAndOpenConsentConfirmation = useCallback(
        (
            { shouldBlockClosingDialog }: DialogControlOptions = {
                shouldBlockClosingDialog: false,
            },
        ) => {
            if (shouldDisplayConsentConfirmation) {
                setOpen(true);
                setShouldKeepOpen(shouldBlockClosingDialog);

                return new Promise((resolve) => {
                    interruptionPromise.current = resolve;
                });
            }

            return Promise.resolve(false);
        },
        [shouldDisplayConsentConfirmation],
    );

    const setIsConsentConfirmationOpened = useCallback(
        (
            shouldOpen: boolean,
            { shouldBlockClosingDialog }: DialogControlOptions = {
                shouldBlockClosingDialog: false,
            },
        ) => {
            setShouldKeepOpen(shouldBlockClosingDialog);
            setOpen(shouldOpen);
        },
        [],
    );

    return (
        <ConsentConfirmationContext.Provider
            value={{
                shouldDisplayConsentConfirmation,
                confirmConsent,
                isNewUser: isNewUnconfirmedUser,
                isConsentConfirmationOpened: open,
                shouldKeepOpen,
                setIsConsentConfirmationOpened,
                shouldInterruptActionAndOpenConsentConfirmation,
                isProcessingConfirmation,
            }}
        >
            {children}
        </ConsentConfirmationContext.Provider>
    );
};

export const useConsentConfirmation = () => {
    const value = useContext(ConsentConfirmationContext);

    if (value === undefined) {
        throw new Error(
            'useConsentConfirmation must be used within a ConsentConfirmationProvider',
        );
    }

    return value;
};
